import { collection, get } from "typesaurus"
import { getDownloadLink } from "../../../snapcourse/services/api"
import { getTimingOptionByValue } from "../../machines/studyPlan/helpers"
import { CONTENT_RESOURCE_SEPARATOR } from "../../../snapcourse/constants"
import { Role } from "../../../mp-database"
import { Roles } from "../../../mp-utils/generic-utils"
import { Media } from "../../../snapcourse/db/collections"
import { EocClientStudyPlan, StudyPlanScope } from "../../types"
import {
  CourseInfo,
  EocClientDraftSection,
  Program,
  RefScope
} from "../../../../types/gqlTypes/graphql"
import { RoutesPathnames, StudyPlanSettingsItem } from "../types"

export const getSubjectPath = (subject: string): string => {
  if (!subject) {
    return null
  }

  return subject
    .replace(/[^a-zA-Z0-9\- ]/g, "")
    .replaceAll(" ", "-")
    .toLowerCase()
}

export const getRoutePathnames = (
  currentProgram: string,
  programCourses: CourseInfo[],
  userRole?: Role
): RoutesPathnames[] => {
  const program = currentProgram
    ? (currentProgram?.toUpperCase() as Program)
    : null
  const subjectsPathnames: RoutesPathnames[] = program
    ? programCourses.map(subject => {
        const path = getSubjectPath(subject.name)

        return {
          name: subject.name,
          path
        }
      }) ?? []
    : []

  return [
    {
      name: "EoC",
      path: "eoc",
      isRoot: true
    },
    {
      name: "Dashboard",
      path: "dashboard",
      isRoot: true
    },
    {
      name: "Study Plans",
      path: "study-plans",
      isDisabled: userRole === "Teacher"
    },
    {
      name: "Course-Class Assignment",
      path: "course-class-assignment"
    },
    {
      name: "Global Study Plan Settings",
      path: "settings"
    },
    {
      name: "Manage",
      path: "manage"
    },
    {
      name: "Recommended",
      path: "recommended",
      isDisabled: true
    },
    {
      name: "View",
      path: "view",
      isDisabled: true
    },
    {
      name: "Edit",
      path: "edit",
      isGuarded: true
    },
    {
      name: "Preview",
      path: "preview"
    },
    ...subjectsPathnames
  ]
}

export const getStudyPlanScopeByUserRole = (
  userRole: string
): StudyPlanScope => {
  switch (userRole) {
    case Roles.Super_Administrator:
    case Roles.District_Admin: {
      return "DISTRICT"
    }
    case Roles.School_Admin: {
      return "SCHOOL"
    }
    case Roles.Teacher: {
      return "TEACHER"
    }
    case Roles.Student: {
      return "STUDENT"
    }
  }
}

export const getPlanRefIdByRefScope = (refScope: RefScope) => {
  switch (refScope.scope) {
    case "DISTRICT": {
      return refScope?.district_id ?? ""
    }
    case "SCHOOL": {
      return refScope?.school_id ?? ""
    }
    case "TEACHER": {
      return refScope?.teacher_id ?? ""
    }
    case "CLASS": {
      return refScope?.class_id ?? ""
    }
    case "STUDENT": {
      return refScope?.student_id ?? ""
    }
  }
}

export const getPlanByScope = (
  studyPlans: { [key: string]: EocClientStudyPlan },
  currentSubject: string,
  scope: StudyPlanScope
): EocClientStudyPlan => {
  const subjectPlan = studyPlans?.[currentSubject]

  if (subjectPlan?.refScope?.scope === scope) {
    return subjectPlan
  }

  return null
}

export const getMasterySectionOrderLabel =
  (planSections: EocClientDraftSection[]) =>
  (sectionId: string): string => {
    const sectionOrder =
      planSections
        ?.filter(section => section.type === "MASTERY_LESSON")
        .findIndex(section => section.id === sectionId) + 1

    return `#${sectionOrder ?? ""}`
  }

interface InfoBoxTextData {
  program: string
  scope: StudyPlanScope
  planScope: StudyPlanScope | ""
  subjectName: string
  isRecommended?: boolean
  isTeacherMasterPlan?: boolean
}

export const getInfoBoxText = (data: InfoBoxTextData): string => {
  const {
    scope,
    planScope,
    subjectName,
    isRecommended,
    isTeacherMasterPlan,
    program
  } = data

  switch (planScope) {
    case "SYSTEM": {
      if (scope === "DISTRICT" || scope === "SCHOOL") {
        return `This is the default ${program} ${subjectName} adaptive study plan. When students log in, this is the study plan they’ll work through. School administrators and teachers can modify this study plan for their students’ needs.`
      }

      if (scope === "TEACHER" && isTeacherMasterPlan) {
        return `This is the default ${program} ${subjectName} adaptive study plan. When students log in, this is the study plan they’ll work through. Teachers can modify this study plan for their students’ needs.`
      }

      break
    }

    case "DISTRICT": {
      if (isRecommended || scope === "TEACHER") {
        return `This is the recommended ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through. School administrators and teachers can customize this study plan for their students’ needs.`
      }

      return `This is a districtwide ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through. School administrators and teachers can customize this study plan for their students’ needs.`
    }

    case "SCHOOL": {
      if (isRecommended || scope === "TEACHER") {
        return `This is the recommended ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through. School administrators and teachers can customize this study plan for their students’ needs.`
      }

      return `This is a schoolwide ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through. Teachers can customize this study plan for their students’ needs.`
    }

    case "TEACHER":
    case "CLASS":
    case "STUDENT": {
      if (isRecommended && isTeacherMasterPlan) {
        return `This is the recommended ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through. Teachers can modify this study plan for their students’ needs.`
      }
      break
    }
    default:
      return null
  }
}

export const getSPV2InfoBoxText = (data: InfoBoxTextData): string[] => {
  const {
    scope,
    planScope,
    subjectName,
    isRecommended,
    isTeacherMasterPlan,
    program
  } = data

  switch (planScope) {
    case "SYSTEM": {
      if (scope === "DISTRICT" || scope === "SCHOOL") {
        return [
          `This is the default ${program} ${subjectName} adaptive study plan. When students log in, this is the study plan they’ll work through.`,
          "School administrators and teachers can modify this study plan for their students’ needs."
        ]
      }

      if (scope === "TEACHER" && isTeacherMasterPlan) {
        return [
          `This is the default ${program} ${subjectName} adaptive study plan. When students log in, this is the study plan they’ll work through.`,
          "Teachers can modify this study plan for their students’ needs."
        ]
      }

      return []
    }

    case "DISTRICT": {
      if (isRecommended || scope === "TEACHER") {
        return [
          `This is the recommended ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through.`,
          "School administrators and teachers can customize this study plan for their students’ needs."
        ]
      }

      return [
        `This is a districtwide ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through.`,
        "School administrators and teachers can customize this study plan for their students’ needs."
      ]
    }

    case "SCHOOL": {
      if (isRecommended || scope === "TEACHER") {
        return [
          `This is the recommended ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through.`,
          "School administrators and teachers can customize this study plan for their students’ needs."
        ]
      }

      return [
        `This is a schoolwide ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through.`,
        "Teachers can customize this study plan for their students’ needs."
      ]
    }

    case "TEACHER":
    case "CLASS":
    case "STUDENT": {
      if (isRecommended && isTeacherMasterPlan) {
        return [
          `This is the recommended ${program} ${subjectName} study plan. When students log in, this is the progression they’ll work through.`,
          "Teachers can modify this study plan for their students’ needs."
        ]
      }

      return []
    }
    default:
      return []
  }
}

export const downloadFileByResourcePath = async (resourcePath: string) => {
  if (!resourcePath) return

  const [docId] = resourcePath.split(CONTENT_RESOURCE_SEPARATOR)

  const doc = await get(collection<Media>("library"), docId)

  const fileUrl = await getDownloadLink(
    doc.data.type,
    doc.data.name,
    doc.data.assets[0].id,
    doc.data.snap_id
  )

  window.open(fileUrl.url, "_blank")
}

export const parseStandards = (standards: Record<string, string[]>) => {
  if (!Object.keys(standards ?? {}).length) return []
  return Object.entries(standards).reduce<string[]>(
    (result, [standard, values]) => {
      if (!values.length) {
        return [...result, standard]
      }

      return [...result, ...values.map(value => [standard, value].join(" "))]
    },
    []
  )
}
export type SettingScope = "global" | "masterPlan" | "plan"

export const settingScopeLabels = {
  global: "Global",
  masterPlan: "Master Plan",
  plan: "Plan"
}

const getRecommendedPart = (value: boolean | number) => {
  switch (typeof value) {
    case "boolean": {
      return `(Recommended: ${value ? "Enabled" : "Disabled"})`
    }
    case "number": {
      return `(Recommended: ${getTimingOptionByValue(value)} time`
    }
    default: {
      return ""
    }
  }
}

const getDescription = (
  settingScope: SettingScope,
  defaultValue?: boolean,
  recommendedValue?: boolean | number
) => {
  if (!defaultValue && !recommendedValue) return ""

  const recommendedPart = getRecommendedPart(recommendedValue)

  const description = `${settingScopeLabels[settingScope]} Default: ${
    defaultValue ? "Enabled" : "Disabled"
  }`

  if (recommendedPart) {
    return description.concat(` ${recommendedPart}`)
  }

  return description
}

type SettingsDefaultValues = {
  studyPlansOutOfOrderDefault: boolean
  resetMasteryLessonsDefault: boolean
  allowPausePracticeTestsDefault: boolean
}

type SettingsRecommendedValues = {
  studentCanDoOutOfOrderRecommended: boolean
  studentCanResetMasteryLessonsRecommended: boolean
}

export const getSettingsSwitches = (
  defaultValues: SettingsDefaultValues,
  withTimingOptions: boolean,
  scope: SettingScope,
  recommendedValues?: SettingsRecommendedValues
) => {
  const {
    studyPlansOutOfOrderDefault,
    resetMasteryLessonsDefault,
    allowPausePracticeTestsDefault
  } = defaultValues

  const {
    studentCanResetMasteryLessonsRecommended,
    studentCanDoOutOfOrderRecommended
  } = recommendedValues ?? {}

  const settingSwitches: StudyPlanSettingsItem[] = [
    {
      key: "studentCanDoOutOfOrder",
      label: "Allow students to complete study plans out of order",
      tooltipText:
        "When enabled, students can complete their study plans in any order they choose. When disabled, students must complete their study plan in order.",
      description: getDescription(
        scope,
        studyPlansOutOfOrderDefault,
        studentCanDoOutOfOrderRecommended
      ),
      defaultValue: studyPlansOutOfOrderDefault
    },
    {
      key: "studentCanResetMasteryLessons",
      label: "Allow students to reset Mastery Lessons",
      tooltipText:
        "When enabled, students can retake any lessons on their study plan. When disabled, students cannot retake any completed lesson of the study plan.",
      description: getDescription(
        scope,
        resetMasteryLessonsDefault,
        studentCanResetMasteryLessonsRecommended
      ),
      defaultValue: resetMasteryLessonsDefault
    }
  ]

  if (withTimingOptions) {
    settingSwitches.push({
      key: "studentCanPauseTests",
      label: "Allow students to pause Practice Tests",
      description: getDescription(scope, allowPausePracticeTestsDefault),
      defaultValue: allowPausePracticeTestsDefault
    })
  }

  return settingSwitches
}
